import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { cn } from "@/lib/utils";
import { VideoProject } from "@/services/bedrock";

import { isKanbanColumnFetchAborted } from "./isKanbanColumnFetchAborted";
import { VideoProjectKanbanColumn } from "./KanbanColumn";
import {
  VideoProjectKanbanCardSkeletonLayout,
  VideoProjectKanbanColumnDef,
  VideoProjectKanbanColumnState,
  VideoProjectKanbanColumnView,
} from "./types";

const UNUSED_QUERY_KEY = ["videoProjects", "kanbanBoard", "unused"] as const;
const EMPTY_PROJECTS: VideoProject[] = [];

export interface VideoProjectKanbanBoardProps<
  Id extends string,
  TData extends VideoProjectKanbanColumnState = VideoProjectKanbanColumnState
> {
  columns: VideoProjectKanbanColumnDef<Id>[];
  className?: string;
  queryKey?: readonly unknown[];
  fetchBoard?: (options: { signal?: AbortSignal }) => Promise<Record<Id, TData>>;
  fetchNextColumnPage?: (
    column: VideoProjectKanbanColumnDef<Id>,
    columnData: TData,
    signal?: AbortSignal
  ) => Promise<TData>;
  getColumnView?: (column: VideoProjectKanbanColumnDef<Id>) => VideoProjectKanbanColumnView;
  loadingMoreColumnId?: Id | null;
  onLoadMore?: (columnId: Id) => void;
  renderCard: (project: VideoProject, column: VideoProjectKanbanColumnDef<Id>) => React.ReactNode;
  cardSkeleton?: VideoProjectKanbanCardSkeletonLayout;
  droppable?: boolean;
  hideEmpty?: boolean;
}

function useKanbanBoardQuery<Id extends string, TData extends VideoProjectKanbanColumnState>(
  queryKey: readonly unknown[] | undefined,
  fetchBoard: ((options: { signal?: AbortSignal }) => Promise<Record<Id, TData>>) | undefined
) {
  const isFetchMode = queryKey != null && fetchBoard != null;
  const query = useQuery({
    queryKey: queryKey ?? UNUSED_QUERY_KEY,
    queryFn: ({ signal }) => fetchBoard!({ signal }),
    enabled: isFetchMode,
    staleTime: 0,
  });

  return {
    isFetchMode,
    boardData: query.data,
    isInitialLoading: isFetchMode && query.isLoading && !query.data,
  };
}

export function VideoProjectKanbanBoard<
  Id extends string,
  TData extends VideoProjectKanbanColumnState = VideoProjectKanbanColumnState
>({
  columns,
  className,
  queryKey,
  fetchBoard,
  fetchNextColumnPage,
  getColumnView,
  loadingMoreColumnId: controlledLoadingMoreColumnId,
  onLoadMore: controlledOnLoadMore,
  renderCard,
  cardSkeleton = "player",
  droppable = false,
  hideEmpty = false,
}: VideoProjectKanbanBoardProps<Id, TData>) {
  const queryClient = useQueryClient();
  const [internalLoadingMoreColumnId, setInternalLoadingMoreColumnId] = React.useState<Id | null>(null);
  const { isFetchMode, boardData, isInitialLoading } = useKanbanBoardQuery(queryKey, fetchBoard);

  const handleFetchLoadMore = React.useCallback(
    async (columnId: Id) => {
      if (!queryKey || !fetchNextColumnPage || internalLoadingMoreColumnId) return;

      const column = columns.find(item => item.id === columnId);
      const columnData = queryClient.getQueryData<Record<Id, TData>>(queryKey)?.[columnId];
      if (!column || !columnData?.hasMore) return;

      setInternalLoadingMoreColumnId(columnId);
      try {
        const nextColumn = await fetchNextColumnPage(column, columnData);
        queryClient.setQueryData<Record<Id, TData>>(queryKey, current => {
          if (!current) return current;
          return { ...current, [columnId]: nextColumn };
        });
      } catch (error) {
        if (!isKanbanColumnFetchAborted(error)) {
          throw error;
        }
      } finally {
        setInternalLoadingMoreColumnId(null);
      }
    },
    [columns, fetchNextColumnPage, internalLoadingMoreColumnId, queryClient, queryKey]
  );

  const loadingMoreColumnId = controlledLoadingMoreColumnId ?? internalLoadingMoreColumnId;
  const onLoadMore = controlledOnLoadMore ?? (isFetchMode ? handleFetchLoadMore : undefined);

  return (
    <div className={cn("flex gap-4 overflow-x-auto pb-4 min-h-[400px]", className)}>
      {columns.map(column => {
        const fetched = boardData?.[column.id];
        const view = getColumnView?.(column) ?? {
          projects: fetched?.projects ?? EMPTY_PROJECTS,
          totalCount: fetched?.totalCount ?? 0,
          hasMore: fetched?.hasMore,
          loading: isInitialLoading,
        };

        return (
          <VideoProjectKanbanColumn
            key={column.id}
            column={column}
            projects={view.projects}
            totalCount={view.totalCount}
            loading={view.loading ?? isInitialLoading}
            hasMore={view.hasMore}
            loadingMore={loadingMoreColumnId === column.id}
            onLoadMore={onLoadMore}
            renderCard={project => renderCard(project, column)}
            cardSkeleton={cardSkeleton}
            headerAccessory={view.headerAccessory}
            headerAccessoryLoading={view.headerAccessoryLoading}
            showCount={view.showCount}
            droppable={droppable}
            dropState={view.dropState}
            hideEmpty={hideEmpty}
            dropPlaceholderIndex={view.dropPlaceholderIndex}
            dropPlaceholderHeight={view.dropPlaceholderHeight}
          />
        );
      })}
    </div>
  );
}
